if (!customElements.get('md-product-pairing')) {
  customElements.define('md-product-pairing', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.cfg = JSON.parse(this.querySelector('[data-config]').textContent);
      this.section = this.closest('.shopify-section');
      this.form = document.getElementById(`product-form-${this.dataset.section}`);
      this.button = this.querySelector('.md-pairing__add');
      this.buttonLabel = this.querySelector('.md-pairing__button-label');
      this.status = this.querySelector('.md-pairing__status');
      this.viewCart = this.querySelector('[data-view-cart]');
      this.selects = [...this.querySelectorAll('[data-variant]')];
      this.busy = false;
      this.pickerReady = !this.section.querySelector('variant-selects');
      this.galleryReady = !this.section.querySelector('swiper-slider-container');
      this.onGalleryReady = () => queueMicrotask(() => {
        if (!this.isConnected) return;
        this.galleryReady = [...this.section.querySelectorAll('swiper-slider-container .swiper')].every(slider => typeof slider.selectSlide === 'function' && typeof slider.swiper?.slideTo === 'function');
        this.render();
      });
      window.addEventListener('swiperLoaded', this.onGalleryReady);
      if (!this.galleryReady) customElements.whenDefined('swiper-slider').then(this.onGalleryReady);
      customElements.whenDefined('variant-selects').then(() => {
        if (!this.isConnected) return;
        this.pickerReady = true;
        this.syncMain();
      });
      this.formatter = new Intl.NumberFormat(this.cfg.locale, {style:'currency', currency:this.cfg.currency});
      this.onChange = this.handleChange.bind(this);
      this.onFormChange = this.syncMain.bind(this);
      this.onSubmit = this.add.bind(this);
      this.addEventListener('change', this.onChange);
      this.button.addEventListener('click', this.onSubmit);
      this.section.addEventListener('change', this.onFormChange);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
          if (event.source !== 'md-product-pairing') this.refreshCart();
        });
      }
      this.onPageShow = () => this.refreshCart();
      window.addEventListener('pageshow', this.onPageShow);
      this.syncMain();
      this.refreshCart();
    }
    disconnectedCallback() {
      this.unsubscribe?.();
      this.section?.removeEventListener('change', this.onFormChange);
      this.removeEventListener('change', this.onChange);
      this.button?.removeEventListener('click', this.onSubmit);
      window.removeEventListener('pageshow', this.onPageShow);
      window.removeEventListener('swiperLoaded', this.onGalleryReady);
      this.initialized = false;
    }
    api(file) {
      return this.cfg.root.replace(/\/?$/, '/') + file;
    }
    async request(file, payload) {
      const controller = new AbortController();
      const remaining = this.actionDeadline ? this.actionDeadline - Date.now() : 10000;
      const timeout = Math.max(1, Math.min(10000, remaining));
      let timer;
      try {
        return await Promise.race([
          (async () => {
            const response = await fetch(this.api(file), {
              credentials:'same-origin', signal:controller.signal,
              headers:{Accept:'application/json', ...(payload ? {'Content-Type':'application/json', 'X-Requested-With':'XMLHttpRequest'} : {})},
              ...(payload ? {method:'POST', body:JSON.stringify(payload)} : {})
            });
            let data;
            try { data = await response.json(); }
            catch { throw new Error(this.cfg.strings.networkError); }
            if (!response.ok || data.status >= 400) {
              const error = new Error(data.description || this.cfg.strings.error);
              error.status = response.status;
              throw error;
            }
            return data;
          })(),
          new Promise((_, reject) => {
            timer = setTimeout(() => {
              controller.abort();
              reject(new Error(this.cfg.strings.networkError));
            }, timeout);
          })
        ]);
      } catch (error) {
        if (!error.status) throw new Error(this.cfg.strings.networkError);
        throw error;
      } finally { clearTimeout(timer); }
    }
    selected() {
      return this.selects.map((select, index) => this.cfg.products[index].variants.find(v => String(v.id) === select.value));
    }
    quantity() {
      const input = this.form?.querySelector('[name="quantity"]');
      const external = this.section.querySelector(`[name="quantity"][form="product-form-${this.dataset.section}"]`);
      return Math.max(1, Number((input || external)?.value) || 1);
    }
    syncMain(event) {
      if (event && !event.target.matches('[name="id"], [name="quantity"]')) return;
      const id = this.form?.querySelector('[name="id"]')?.value;
      if (this.cfg.products[0].variants.some(v => String(v.id) === id)) this.selects[0].value = id;
      this.render();
    }
    setStatus(text = '') {
      this.status.textContent = text;
      this.status.hidden = !text;
      if (!text && this.viewCart) this.viewCart.hidden = true;
    }
    otherOffer(cart) {
      const code = this.cfg.offerCode.toUpperCase();
      const codes = (cart?.discount_codes || []).filter(d => d.applicable !== false);
      const allocations = [...(cart?.cart_level_discount_applications || []), ...(cart?.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(d => d.discount_application))];
      return codes.some(d => d.code.toUpperCase() !== code) || allocations.some(d => String(d.title).toUpperCase() !== code);
    }
    offerApplied(cart) {
      const matches = d => String(d?.title).toUpperCase() === this.cfg.offerCode.toUpperCase() && d.value_type === 'percentage' && Number(d.value) === this.cfg.offerPercent;
      return (cart.cart_level_discount_applications || []).some(d => matches(d) && d.total_allocated_amount > 0) || (cart.items || []).some(item => (item.line_level_discount_allocations || []).some(d => matches(d.discount_application) && d.amount > 0));
    }
    render() {
      const variants = this.selected();
      variants.forEach((variant, index) => {
        if (!variant) return;
        const card = this.selects[index].closest('.md-pairing__product');
        card.querySelector('[data-selected]').textContent = variant.title;
        const image = card.querySelector('img');
        if (image && image.getAttribute('src') !== variant.image) {
          image.removeAttribute('srcset');
          image.src = variant.image;
          image.alt = variant.title;
        }
        card.querySelector('[data-quantity]').textContent = index === 0 && this.quantity() > 1 ? `× ${this.quantity()}` : '';
      });
      if (variants.some(v => !v)) return;
      const original = variants[0].price * this.quantity() + variants[1].price;
      const offer = !!this.cfg.offerCode && this.cfg.offerPercent > 0 && !this.otherOffer(this.cart) && !this.offerFailed;
      const total = offer ? Math.round(variants[0].price * (100 - this.cfg.offerPercent) / 100) * this.quantity() + Math.round(variants[1].price * (100 - this.cfg.offerPercent) / 100) : original;
      this.querySelector('[data-total]').textContent = this.formatter.format(total / 100);
      this.querySelector('[data-original]').textContent = this.formatter.format(original / 100);
      this.querySelector('.md-pairing__offer').hidden = !offer;
      this.buttonLabel.textContent = this.busy ? this.cfg.strings.adding : this.cfg.strings.addBoth;
      this.button.disabled = this.busy || !this.pickerReady || variants.some(v => !v.available);
      this.button.setAttribute('aria-busy', String(this.busy));
      this.selects.forEach((select, index) => { select.disabled = this.busy || (index === 0 && (!this.pickerReady || !this.galleryReady)); });
    }
    async refreshCart() {
      if (this.busy || this.refreshing) return;
      this.refreshing = true;
      try { this.cart = await this.request('cart.js'); if (this.offerApplied(this.cart)) this.offerFailed = false; this.render(); }
      catch { this.setStatus(this.cfg.strings.error); }
      finally { this.refreshing = false; }
    }
    lockMain() {
      const controls = [...(this.section.querySelector('variant-selects')?.querySelectorAll('input,select') || []), ...(this.form?.querySelectorAll('button,input[name="quantity"]') || [])];
      this.locked = controls.map(control => [control, control.disabled]);
      controls.forEach(control => { control.disabled = true; });
    }
    unlockMain() {
      this.locked?.forEach(([control, disabled]) => { if (control.isConnected) control.disabled = disabled; });
      this.locked = null;
    }
    async handleChange(event) {
      if (!event.target.matches('[data-variant]')) return;
      this.offerFailed = false;
      this.setStatus();
      if (event.target.dataset.variant === 'paired') { this.render(); return; }
      const id = event.target.value;
      const picker = this.section.querySelector('variant-selects');
      if (!picker?.updateSelectedVariant) { this.syncMain(); this.setStatus(this.cfg.strings.variantError); return; }
      this.busy = true;
      this.lockMain();
      this.render();
      try {
        await picker.updateSelectedVariant({target:{options:[{dataset:{variantId:id}}], selectedIndex:0, dataset:{variantId:id}}});
        if (this.form.querySelector('[name="id"]').value !== id) throw new Error();
      } catch { this.setStatus(this.cfg.strings.variantError); }
      finally { this.unlockMain(); this.busy = false; this.syncMain(); }
    }
    async add() {
      if (this.busy || this.button.disabled) return;
      const mainButton = this.form?.querySelector('[type="submit"]');
      if (mainButton?.classList.contains('loading')) return;
      this.busy = true;
      this.actionDeadline = Date.now() + 15000;
      this.offerFailed = false;
      this.setStatus();
      this.lockMain();
      this.render();
      const drawer = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const sections = [...new Set((drawer?.getSectionsToRender?.() || []).map(s => s.id))].slice(0,5);
      const context = {sections, sections_url:window.location.pathname};
      let added = false;
      let response;
      const items = this.selected().map((variant, index) => ({id:variant.id, quantity:index === 0 ? this.quantity() : 1}));
      try {
        if (this.form && !this.form.checkValidity()) { this.form.reportValidity(); return; }
        drawer?.setActiveElement?.(this.button);
        response = await this.request('cart/add.js', {items, ...context});
        added = true;
        this.cart = await this.request('cart.js');
        let rendered = response;
        if (this.cfg.offerCode && this.cfg.offerPercent > 0 && !this.otherOffer(this.cart) && !this.offerApplied(this.cart)) {
          try {
            const update = await this.request('cart/update.js', {discount:this.cfg.offerCode, ...context});
            this.cart = await this.request('cart.js');
            rendered = {...this.cart, sections:update.sections};
            if (!this.offerApplied(this.cart)) { this.offerFailed = true; this.setStatus(this.cfg.strings.offerError); }
          } catch { this.offerFailed = true; this.setStatus(this.cfg.strings.offerError); }
        } else if (this.otherOffer(this.cart)) this.setStatus(this.cfg.strings.keepOffer);
        if (drawer && sections.every(id => rendered.sections?.[id])) {
          drawer.classList.remove('is-empty');
          drawer.renderContents({...rendered, id:items[0].id});
        } else if (!drawer) window.location.assign(window.routes?.cart_url || this.api('cart'));
        else { drawer.open?.(); this.setStatus(this.cfg.strings.added); }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, {source:'md-product-pairing', cartData:this.cart});
      } catch (error) {
        if (added) {
          this.offerFailed = true;
          try {
            if (drawer && sections.length && sections.every(id => response.sections?.[id])) {
              drawer.classList.remove('is-empty');
              drawer.renderContents({...response, id:items[0].id});
            }
          } catch {}
        }
        this.setStatus(added ? this.cfg.strings.cartError : error.message || this.cfg.strings.networkError);
        if (this.viewCart) this.viewCart.hidden = false;
      } finally {
        this.actionDeadline = null;
        this.unlockMain();
        this.busy = false;
        this.render();
      }
    }
  });
}
